import { Promotion } from './promotions.interface';

export enum PromoCodeStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  SCHEDULED = 'SCHEDULED',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
  /** Only applies when usageLimitType is TOTAL_USES and the limit has been reached. */
  DEPLETED = 'DEPLETED',
}

export enum PromoCodeAccessType {
  PUBLIC = 'PUBLIC',
  RESTRICTED = 'RESTRICTED',
}

export enum PromoCodeUsageLimitType {
  UNLIMITED = 'UNLIMITED',
  PER_CUSTOMER = 'PER_CUSTOMER',
  TOTAL_USES = 'TOTAL_USES',
}

/** Promo code as returned by the backend. `status` is a computed value. */
export interface PromoCode {
  promoCodeId: string;
  code: string;
  companyId: string;
  promotionId: string;
  accessType: PromoCodeAccessType;
  usageLimitType: PromoCodeUsageLimitType;
  validFrom?: string;
  validUntil?: string;
  publishedAt?: string;
  allowedUsers?: string[];
  usesPerCustomer?: number;
  totalUsesLimit?: number;
  currentUses?: number;
  status: PromoCodeStatus;
}

export interface PromoCodeList {
  totalCount: number;
  entities: PromoCode[];
}

/** `companyId` is passed as a query parameter, not in the request body. */
export interface CreatePromoCodeRequest {
  code: string;
  promotionId: string;
  accessType: PromoCodeAccessType;
  usageLimitType: PromoCodeUsageLimitType;
  validFrom: string | null;
  validUntil: string | null;
  allowedUsers: string[] | null;
  usesPerCustomer: number | null;
  totalUsesLimit: number | null;
}

/**
 * Partial update of an existing promo code. Every field is optional; only DRAFT codes
 * can be edited (the backend silently no-ops updates on other statuses). `companyId` is
 * passed as a query parameter, not in the body.
 */
export interface UpdatePromoCodeRequest {
  code?: string;
  accessType?: PromoCodeAccessType;
  usageLimitType?: PromoCodeUsageLimitType;
  allowedUsers?: string[] | null;
  usesPerCustomer?: number | null;
  totalUsesLimit?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
}

/** Local state for a promo code card that is being created (before it is persisted). */
export interface PromoCodeDraft {
  localId: string;
  code: string;
  validFrom: Date | null;
  validUntil: Date | null;
}

export interface PromoCodeErrors {
  code?: string;
  validFrom?: string;
  validUntil?: string;
}

export type PromoCodeErrorField = keyof PromoCodeErrors;

export interface PromoCodesSectionProps {
  /** The saved promotion; `null` until the promotion details have been saved. */
  promotion: Promotion | null;
  companyId: string;
  /** Whether the current user can create/manage promo codes. */
  canManage: boolean;
  /**
   * When true (arriving via the "Add code" deep-link), open a fresh draft code card
   * automatically so the user lands directly on the code form.
   */
  autoAddCode?: boolean;
  onValidationChange?: (hasErrors: boolean) => void;
}
